import { IComparableProperty } from '@/components/ComparableSales';
import { AIComparableProperty } from '@/components/TaxReport/AIComparables';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import getApiBaseUrl from '@/utils/getBaseUrl';

export interface Report {
  address: string;
  images?: string[];
  comparables: IComparableProperty[];
  aiComparables?: AIComparableProperty[];
  staticAppeal: {
    apn: string;
    assessorOfficeName: string;
    countyName: string;
    legalDescription: string;
    mailingAddress: string;
    opinionOfValue: string;
    subjectPropertyAddress: string;
  };
  comparableNotes: string;
  staticInstructions: {
    countyName: string;
    phone: string;
    portalUrl: string;
    statuteUrl: string;
    websiteUrl: string;
    addressLines: string[];
  };
  createdAt: string;
  propertyInfo: {
    airConditioningAvailable: boolean | null;
    airConditioningType: string | null;
    heatingFuelType: string | null;
    heatingType: string | null; // e.g., 'Forced Air Unit'
    utilitiesSewageUsage: string | null;
    utilitiesWaterSource: string | null;
    // --- Structural Features ---
    attic: boolean;
    breezeway: boolean;
    deck: boolean;
    deckArea: number; // in sq ft
    patio: boolean;
    patioArea: number | string | null; // The example showed '0' (string) so this allows for number | string
    pool: boolean;
    poolArea: number;
    porchArea: number | null;
    porchType: string | null;
    rvParking: boolean;
    // --- Building/Material Details ---
    construction: string | null; // e.g., 'Frame'
    interiorStructure: string | null; // e.g., 'Gypsum Board/Drywall/Sheetrock/Wallboard'
    roofConstruction: string | null;
    roofMaterial: string | null; // e.g., '109'
    stories: number | string | null; // Could be a number or a string description
    yearBuilt: number;
    // --- Basement Details ---
    basementFinishedPercent: number; // e.g., 100
    basementSquareFeet: number;
    basementSquareFeetFinished: number;
    basementSquareFeetUnfinished: number;
    basementType: string | null; // e.g., 'PARTIALLY FINISHED'
    // --- Room/Size Counts ---
    bathrooms: number;
    partialBathrooms: number;
    bedrooms: number;
    buildingSquareFeet: number; // Total building sq ft
    livingSquareFeet: number; // Above ground/living sq ft
    lotSquareFeet: number;
    plumbingFixturesCount: number;
    roomsCount: number;
    unitsCount: number;
    // --- Parking/Garage ---
    carport: boolean;
    garageSquareFeet: number;
    garageType: string | null; // e.g., 'Attached Garage'
    parkingSpaces: number;
    // --- Fireplace/Safety ---
    featureBalcony: boolean;
    fireplace: boolean;
    fireplaces: number | null;
    safetyFireSprinklers: boolean;
    // --- Financial/Legal/Metadata ---
    marketValue: number;
    pricePerSquareFoot: number;
    propertyUse: string; // e.g., 'Single Family Residence'
    propertyUseCode: number;
    hoa: boolean;
    taxExemptionHomeownerFlag: boolean;
    buildingCondition: string | null;
    buildingsCount: number;
    legalDescription: string | null;
    mls: string | null;
    parcelAccountNumber: string | null;
    // --- Location ---
    latitude: number;
    longitude: number;
    address: {
      address: string | null; // Note: 'address' is a property here AND a key in the outer object's value
      carrierRoute: string | null;
      city: string; // e.g., 'Superior' - should be generic string
      congressionalDistrict: string | null; // e.g., '02'
      county: string; // e.g., 'Boulder County'
      fips: string; // FIPS code, e.g., '08013'
      house: string | null; // Street number, e.g., '1029'
      jurisdiction: string | null;
      label: string; // Full address label, e.g., '1029 Topaz St, Superior, CO 80027'
      preDirection: string | null;
      state: string; // e.g., 'CO'
      street: string | null;
      streetType: string | null; // e.g., 'St'
      unit: string | null;
      unitType: string | null;
      zip: string; // e.g., '80027'
      zip4: string | null;
    };
  };
}

export default function useGetReport(id: string) {
  const { isAuthenticated } = useAuth0();
  const token = window.localStorage.getItem('_token');

  return useQuery<Report>({
    queryKey: ['REPORTS', id],
    queryFn: async () => {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/reports/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.statusText}`);
      }

      return (await response.json()).data;
    },
    enabled: isAuthenticated && !!token && !!id,
  });
}
